import React from 'react';
import { motion } from 'framer-motion';
// Shared animated background component with fractal mycelium network
export function AnimatedBackground() {
  // Generate random positions and sizes for nodes across full viewport
  const generateNodes = (count: number) => {
    return Array.from({
      length: count
    }, () => ({
      cx: Math.random() * 100,
      cy: Math.random() * 100,
      r: 1.5 + Math.random() * 4,
      delay: Math.random() * 5,
      duration: 2 + Math.random() * 4
    }));
  };
  const nodes = generateNodes(60);
  // Generate fractal branching pattern (recursive-like structure)
  const generateFractalBranch = (startX: number, startY: number, angle: number, length: number, depth: number, maxDepth: number = 3): string => {
    if (depth >= maxDepth || length < 50) return '';
    const endX = startX + Math.cos(angle) * length;
    const endY = startY + Math.sin(angle) * length;
    // Create curved path using quadratic bezier
    const controlX = startX + Math.cos(angle) * length * 0.5 + (Math.random() - 0.5) * 30;
    const controlY = startY + Math.sin(angle) * length * 0.5 + (Math.random() - 0.5) * 30;
    let path = `M${startX},${startY} Q${controlX},${controlY} ${endX},${endY}`;
    // Branch into 2-3 sub-branches with varying angles
    const numBranches = 2 + Math.floor(Math.random() * 2);
    for (let i = 0; i < numBranches; i++) {
      const branchAngle = angle + (Math.random() - 0.5) * Math.PI * 0.6;
      const branchLength = length * (0.6 + Math.random() * 0.3);
      path += ' ' + generateFractalBranch(endX, endY, branchAngle, branchLength, depth + 1, maxDepth);
    }
    return path;
  };
  // Generate multiple fractal networks across entire viewport (not just top-left)
  const generateFractalNetworks = (count: number) => {
    return Array.from({
      length: count
    }, () => {
      // Fully randomized starting positions across entire viewport
      const startX = Math.random() * 100;
      const startY = Math.random() * 100;
      const angle = Math.random() * Math.PI * 2;
      const length = 60 + Math.random() * 100;
      return {
        path: generateFractalBranch(startX, startY, angle, length, 0, 2),
        delay: Math.random() * 5,
        duration: 8 + Math.random() * 6
      };
    });
  };
  const fractalNetworks = generateFractalNetworks(25);
  // Generate organic curved connections spanning full viewport
  const generateOrganicConnections = (count: number) => {
    return Array.from({
      length: count
    }, () => {
      const x1 = Math.random() * 100;
      const y1 = Math.random() * 100;
      const x2 = Math.random() * 100;
      const y2 = Math.random() * 100;
      // Create multiple control points for more organic curves
      const controlX1 = (x1 + x2) / 2 + (Math.random() - 0.5) * 40;
      const controlY1 = (y1 + y2) / 2 + (Math.random() - 0.5) * 40;
      const controlX2 = (x1 + x2) / 2 + (Math.random() - 0.5) * 40;
      const controlY2 = (y1 + y2) / 2 + (Math.random() - 0.5) * 40;
      return {
        path: `M${x1},${y1} C${controlX1},${controlY1} ${controlX2},${controlY2} ${x2},${y2}`,
        delay: Math.random() * 6,
        duration: 10 + Math.random() * 8
      };
    });
  };
  const organicConnections = generateOrganicConnections(35);
  // Generate randomized stream paths spanning entire viewport
  const generateStreamPaths = (count: number) => {
    return Array.from({
      length: count
    }, () => {
      const startX = Math.random() * 120 - 10;
      const startY = Math.random() * 120 - 10;
      const endX = Math.random() * 120 - 10;
      const endY = Math.random() * 120 - 10;
      // Create flowing path with multiple curves
      const points = [];
      const numPoints = 3 + Math.floor(Math.random() * 4);
      for (let i = 0; i <= numPoints; i++) {
        const t = i / numPoints;
        const x = startX + (endX - startX) * t + (Math.random() - 0.5) * 35;
        const y = startY + (endY - startY) * t + (Math.random() - 0.5) * 35;
        points.push({
          x,
          y
        });
      }
      // Build smooth path through points
      let path = `M${points[0].x},${points[0].y}`;
      for (let i = 1; i < points.length; i++) {
        if (i === 1) {
          path += ` Q${points[i].x},${points[i].y}`;
        } else {
          path += ` T${points[i].x},${points[i].y}`;
        }
      }
      return {
        path,
        duration: 8 + Math.random() * 15,
        delay: Math.random() * 10,
        opacity: 0.3 + Math.random() * 0.5,
        size: 0.25 + Math.random() * 0.4
      };
    });
  };
  const streamPaths = generateStreamPaths(25);
  // Generate main highway paths across entire viewport
  const generateHighwayPaths = (count: number) => {
    return Array.from({
      length: count
    }, () => {
      const startEdge = Math.floor(Math.random() * 4); // 0=top, 1=right, 2=bottom, 3=left
      let startX, startY, endX, endY;
      switch (startEdge) {
        case 0:
          // top
          startX = Math.random() * 100;
          startY = -10;
          endX = Math.random() * 100;
          endY = 110;
          break;
        case 1:
          // right
          startX = 110;
          startY = Math.random() * 100;
          endX = -10;
          endY = Math.random() * 100;
          break;
        case 2:
          // bottom
          startX = Math.random() * 100;
          startY = 110;
          endX = Math.random() * 100;
          endY = -10;
          break;
        default:
          // left
          startX = -10;
          startY = Math.random() * 100;
          endX = 110;
          endY = Math.random() * 100;
      }
      // Create flowing path with curves
      const midX1 = (startX + endX) / 2 + (Math.random() - 0.5) * 45;
      const midY1 = (startY + endY) / 2 + (Math.random() - 0.5) * 45;
      const midX2 = (startX + endX) / 2 + (Math.random() - 0.5) * 45;
      const midY2 = (startY + endY) / 2 + (Math.random() - 0.5) * 45;
      return {
        path: `M${startX},${startY} Q${midX1},${midY1} ${(startX + endX) / 2},${(startY + endY) / 2} T${endX},${endY}`,
        duration: 5 + Math.random() * 4,
        delay: Math.random() * 3,
        opacityRange: [0, 0.4 + Math.random() * 0.3, 0.4 + Math.random() * 0.3, 0.2 + Math.random() * 0.2]
      };
    });
  };
  const highwayPaths = generateHighwayPaths(10);
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-23">
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-24">
        <defs data-id="element-25">
          <linearGradient id="myceliumGradient1" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-26">
            <stop offset="0%" stopColor="#F3F5F1" stopOpacity="0.6" data-id="element-27" />
            <stop offset="50%" stopColor="#e8f5e9" stopOpacity="0.4" data-id="element-28" />
            <stop offset="100%" stopColor="#F3F5F1" stopOpacity="0.2" data-id="element-29" />
          </linearGradient>
          <linearGradient id="myceliumGradient2" x1="100%" y1="0%" x2="0%" y2="100%" data-id="element-30">
            <stop offset="0%" stopColor="#e8f5e9" stopOpacity="0.5" data-id="element-31" />
            <stop offset="50%" stopColor="#d4f4dd" stopOpacity="0.3" data-id="element-32" />
            <stop offset="100%" stopColor="#F3F5F1" stopOpacity="0.15" data-id="element-33" />
          </linearGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%" data-id="element-34">
            <feGaussianBlur stdDeviation="0.3" result="blur" data-id="element-35" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" data-id="element-36" />
          </filter>
        </defs>

        {/* Fractal branching networks - now spanning entire viewport */}
        <g stroke="url(#myceliumGradient1)" strokeWidth="0.15" fill="none" strokeLinecap="round" data-id="element-37">
          {fractalNetworks.map((network, i) => <motion.path key={`fractal-${i}`} d={network.path} initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: [0, 0.5, 0.5, 0.3]
        }} transition={{
          pathLength: {
            duration: network.duration,
            ease: 'easeInOut'
          },
          opacity: {
            duration: network.duration * 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: network.delay
          }
        }} data-id="element-38" />)}
        </g>

        {/* Organic connecting threads spanning full viewport */}
        <g stroke="url(#myceliumGradient2)" strokeWidth="0.12" fill="none" strokeLinecap="round" data-id="element-39">
          {organicConnections.map((conn, i) => <motion.path key={`organic-${i}`} d={conn.path} initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: [0, 1, 1, 0],
          opacity: [0, 0.4, 0.4, 0]
        }} transition={{
          duration: conn.duration,
          repeat: Infinity,
          delay: conn.delay,
          ease: 'easeInOut'
        }} data-id="element-40" />)}
        </g>

        {/* Main flowing mycelium highways spanning entire viewport */}
        <g stroke="url(#myceliumGradient1)" strokeWidth="0.2" fill="none" strokeLinecap="round" data-id="element-41">
          {highwayPaths.map((highway, i) => <motion.path key={`highway-${i}`} d={highway.path} initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: highway.opacityRange
        }} transition={{
          pathLength: {
            duration: highway.duration,
            ease: 'easeInOut'
          },
          opacity: {
            duration: highway.duration * 2,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: highway.delay
          }
        }} data-id="element-42" />)}
        </g>

        {/* Randomized light pulses traveling along varied paths across entire page */}
        {streamPaths.map((stream, i) => <motion.circle key={`stream-${i}`} r={stream.size} fill="#4DE7D4" filter="url(#glow)" opacity={stream.opacity} data-id="element-43">
            <animateMotion dur={`${stream.duration}s`} repeatCount="indefinite" begin={`${stream.delay}s`} path={stream.path} data-id="element-44" />
            <animate attributeName="opacity" values={`0;${stream.opacity};0`} dur={`${stream.duration}s`} repeatCount="indefinite" begin={`${stream.delay}s`} data-id="element-45" />
          </motion.circle>)}

        {/* Randomized pulsing nodes across full viewport */}
        {nodes.map((node, i) => <motion.circle key={i} cx={node.cx} cy={node.cy} r={node.r * 0.08} fill="#F3F5F1" initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: [0, 0.7, 0],
        scale: [0, 1.4, 0]
      }} transition={{
        duration: node.duration,
        repeat: Infinity,
        delay: node.delay,
        ease: 'easeInOut'
      }} data-id="element-46" />)}
      </svg>

      {/* Radial gradient overlay for depth */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(243,245,241,0.04)_0%,transparent_70%)]" data-id="element-47" />
    </div>;
}